import { useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { ApiError } from "@/api/client";
import {
  useAdminDecideLeave,
  useAdminLeave,
  useAdminLeaveMonthlySummary,
  useAdminLeaveSummary,
  useAllWfh,
  useLogLeaveForUser,
  useSetLeaveBalances,
} from "@/api/queries";
import { useSession } from "@/api/session";
import {
  SETTABLE_BALANCE_TYPES,
  type LeaveBalanceLine,
  type LeaveRequest,
  type LeaveStatus,
  type LeaveSummaryMember,
  type LeaveType,
  type SessionUser,
  type SettableBalanceType,
  type WfhRequest,
} from "@/api/types";
import { PageHeader } from "@/app/page-header";
import { CategoryBarChart, TrendChart } from "@/components/charts";
import { Avatar } from "@/components/ui/avatar";
import { Badge, FiledLateBadge, StatusBadge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { Field, Input, Select, Textarea } from "@/components/ui/field";
import { Icon } from "@/components/ui/icon";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { EmptyState, ErrorState, SkeletonTable } from "@/components/ui/states";
import { TBody, TD, TDPrimary, TH, THead, TR, TableWrap } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/toast";
import { approvedDaysBy, approvedDaysInYear, leaveDaysByMonth } from "@/lib/analytics";
import { calendarTodayStr, currentMonth, monthBounds, shiftDate, todayStr } from "@/lib/calendar";
import { cn } from "@/lib/cn";
import { orgYear } from "@/lib/org-time";
import { formatDate, formatDays, formatTimestampDate } from "@/lib/format";
import { EMPLOYMENT_TYPE_LABEL, LEAVE_TYPE_SHORT, leaveLabel, UNRECORDED_ABSENCE_LABEL } from "@/lib/labels";
import { LeaveDetailDialog, ReasonCell } from "@/pages/shared/leave-detail-dialog";
import { MonthStepper } from "@/pages/shared/month-stepper";
import { WfhDecideDialog, WfhRequestTable, type WfhConfirming } from "@/pages/shared/wfh-approvals";
import { WfhWeekCharts } from "@/pages/shared/wfh-charts";

const MONTHLY_SUMMARY_TYPE_ORDER: LeaveType[] = Object.keys(LEAVE_TYPE_SHORT) as LeaveType[];

/** How many rows of the request log to show before asking for more. */
const REQUEST_PAGE_SIZE = 50;

/** How far ahead "Who's away" looks, in calendar days. */
const AWAY_LOOKAHEAD_DAYS = 7;

/** Keeps the name in view while a wide table scrolls sideways. */
const STICKY_HEAD = "sticky left-0 z-20 bg-surface-sunken";
const STICKY_CELL = "sticky left-0 z-[1] bg-surface";

/** Sets the Unrecorded column apart from the leave columns before it. */
const UNRECORDED_COL = "border-l-2 border-line-strong";

// Work from home used to be a fifth tab. Its approvals now sit in the same
// "Waiting on you" list as leave, so an old ?tab=wfh link lands on Requests.
type TabKey = "requests" | "balances" | "monthly" | "insights";
const TAB_KEYS: TabKey[] = ["requests", "balances", "monthly", "insights"];

/** "all", or a "YYYY-MM" month — what the Requests tab's lists are scoped to. */
type MonthScope = string;
const ALL_MONTHS = "all";

/** Does this span have any day inside the scope? */
function inScope(item: { start_date: string; end_date: string }, scope: MonthScope): boolean {
  if (scope === ALL_MONTHS) return true;
  const { start, end } = monthBounds(scope);
  return item.start_date <= end && item.end_date >= start;
}

/** Pending first — those still need someone — then latest dates first. */
function byPendingThenNewest<T extends { status: string; start_date: string; id: number }>(a: T, b: T): number {
  const pa = a.status === "pending" ? 0 : 1;
  const pb = b.status === "pending" ? 0 : 1;
  return pa - pb || b.start_date.localeCompare(a.start_date) || b.id - a.id;
}

type Confirming = { request: LeaveRequest; status: "approved" | "rejected" };

/** Manager requests are decidable by any admin; the primary HR & Admin
 *  account's own requests only by a People-Function approver, never by
 *  themself. Mirrors the authorization rule enforced server-side in
 *  POST /api/admin/leave/:id/decide — this only controls button visibility. */
function canDecide(request: LeaveRequest, currentUser: { id: number; is_people_function_approver: boolean }) {
  if (request.status !== "pending") return false;
  if (request.employee_role === "manager") return true;
  if (
    request.employee_role === "admin" &&
    !request.employee_is_people_function_approver &&
    request.user_id !== currentUser.id &&
    currentUser.is_people_function_approver
  ) {
    return true;
  }
  return false;
}

/** Who a pending request is waiting on, when it isn't waiting on this admin. */
function waitingOnLabel(request: LeaveRequest, currentUserId: number | undefined): string {
  if (request.user_id === currentUserId) return "Your own request";
  if (request.employee_role === "employee") return "With their manager";
  return "With a People-Function approver";
}

function teamOf(item: { team_name?: string | null }): string {
  return item.team_name ?? "Unassigned";
}

function matchesSearch(name: string | null | undefined, query: string): boolean {
  const q = query.trim().toLowerCase();
  return q === "" || (name ?? "").toLowerCase().includes(q);
}

function dateSpan(start: string, end: string, pattern = "d MMM"): string {
  const from = formatDate(start, pattern) ?? start;
  return start === end ? from : `${from} – ${formatDate(end, pattern) ?? end}`;
}

/*
  The page is split by the job HR is doing, not stacked into one long scroll:
  deciding (Requests), correcting (Balances), reporting (Monthly summary) and
  looking for patterns (Insights). The tab lives in the URL so a link, a reload
  or the back button lands on the same view.
*/
export function HrLeavePage() {
  const leave = useAdminLeave();
  const summary = useAdminLeaveSummary();
  const wfh = useAllWfh();
  const decide = useAdminDecideLeave();
  const currentUser = useSession((s) => s.user);
  const [params, setParams] = useSearchParams();
  const tabParam = params.get("tab") as TabKey | null;
  const tab: TabKey = tabParam && TAB_KEYS.includes(tabParam) ? tabParam : "requests";

  const [confirming, setConfirming] = useState<Confirming | null>(null);
  const [wfhConfirming, setWfhConfirming] = useState<WfhConfirming | null>(null);
  const [logging, setLogging] = useState(false);
  const [editingBalancesFor, setEditingBalancesFor] = useState<number | null>(null);
  const [viewing, setViewing] = useState<LeaveRequest | null>(null);
  // One month for both lists on the Requests tab, so leave and work from home
  // are always being read for the same period.
  const [month, setMonth] = useState<MonthScope>(currentMonth());

  const requests = leave.data ?? [];
  const wfhRequests = wfh.data ?? [];
  const members = summary.data?.members ?? [];

  const decidable = useMemo(
    () => (currentUser ? requests.filter((r) => canDecide(r, currentUser)) : []),
    [requests, currentUser],
  );
  // Same rule WfhApprovalsPanel applies: anyone's pending request but your own.
  const wfhDecidable = useMemo(
    () => wfhRequests.filter((r) => r.status === "pending" && r.user_id !== currentUser?.id),
    [wfhRequests, currentUser],
  );

  function setTab(next: string) {
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        if (next === "requests") out.delete("tab");
        else out.set("tab", next);
        return out;
      },
      { replace: true },
    );
  }

  async function onDecide() {
    if (!confirming) return;
    try {
      await decide.mutateAsync({ id: confirming.request.id, status: confirming.status });
      toast.success(
        `Leave ${confirming.status} for ${confirming.request.employee_name ?? "this person"}.`,
      );
      setConfirming(null);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not record that decision.");
    }
  }

  if (leave.error) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader eyebrow="Org-wide" title="Leave" />
        <Panel>
          <ErrorState message={leave.error.message} onRetry={() => void leave.refetch()} />
        </Panel>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Org-wide"
        title="Leave"
        sub="Employee leave is decided by their own manager. Manager leave, the primary HR & Admin account's own, and anyone's work from home are decided here."
        actions={
          <Button variant="primary" iconLeft="plus" onClick={() => setLogging(true)}>
            Log leave
          </Button>
        }
      />

      <Tabs value={tab} onValueChange={setTab}>
        <div className="-mx-1 overflow-x-auto px-1 pb-1">
          <TabsList className="[&>*]:whitespace-nowrap">
            <TabsTrigger value="requests">
              <TabLabel count={decidable.length + wfhDecidable.length}>Requests</TabLabel>
            </TabsTrigger>
            <TabsTrigger value="balances">Balances</TabsTrigger>
            <TabsTrigger value="monthly">Monthly summary</TabsTrigger>
            <TabsTrigger value="insights">Insights</TabsTrigger>
          </TabsList>
        </div>

        {/* data-[state=active] rather than a bare `flex`: an inactive tab keeps
            its element with the `hidden` attribute, and `flex` would override it. */}
        <TabsContent value="requests" className="mt-4 flex-col gap-6 data-[state=active]:flex">
          <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
            <DecisionQueue
              loading={leave.isLoading || wfh.isLoading}
              requests={decidable}
              wfhRequests={wfhDecidable}
              onDecide={setConfirming}
              onDecideWfh={setWfhConfirming}
              onView={setViewing}
            />
            <AwayPanel loading={leave.isLoading} requests={requests} onView={setViewing} />
          </div>
          <RequestLog
            loading={leave.isLoading}
            requests={requests}
            month={month}
            onMonthChange={setMonth}
            currentUser={currentUser}
            onDecide={setConfirming}
            onView={setViewing}
          />
          <WfhSection
            loading={wfh.isLoading}
            error={wfh.error}
            onRetry={() => void wfh.refetch()}
            requests={wfhRequests}
            month={month}
            currentUserId={currentUser?.id}
            onDecide={setWfhConfirming}
          />
        </TabsContent>

        <TabsContent value="balances" className="mt-4">
          <BalancesPanel
            loading={summary.isLoading}
            error={summary.error}
            onRetry={() => void summary.refetch()}
            members={members}
            year={summary.data?.year ?? orgYear()}
            onEdit={setEditingBalancesFor}
          />
        </TabsContent>

        <TabsContent value="monthly" className="mt-4">
          <MonthlySummaryPanel />
        </TabsContent>

        <TabsContent value="insights" className="mt-4">
          <InsightsView
            loading={leave.isLoading}
            summaryLoading={summary.isLoading}
            requests={requests}
            members={members}
            year={summary.data?.year ?? orgYear()}
          />
        </TabsContent>
      </Tabs>

      {wfhConfirming && (
        <WfhDecideDialog confirming={wfhConfirming} onClose={() => setWfhConfirming(null)} />
      )}

      {confirming && (
        <Dialog open onOpenChange={(open) => !open && setConfirming(null)}>
          <DialogContent>
            <DialogHeader
              title={confirming.status === "approved" ? "Approve this leave?" : "Decline this leave?"}
              description={`${confirming.request.employee_name ?? "This person"} · ${leaveLabel(
                confirming.request.leave_type,
              )} · ${formatDate(confirming.request.start_date)}${
                confirming.request.start_date !== confirming.request.end_date
                  ? ` to ${formatDate(confirming.request.end_date)}`
                  : ""
              }`}
            />
            <DialogBody>
              <dl className="divide-y divide-line rounded-lg border border-line">
                <div className="flex justify-between gap-4 px-3 py-2">
                  <dt className="text-[13px] text-muted">Days</dt>
                  <dd className="text-[13px] font-medium text-ink">
                    {confirming.request.days}
                  </dd>
                </div>
                <div className="flex justify-between gap-4 px-3 py-2">
                  <dt className="text-[13px] text-muted">Reason</dt>
                  <dd className="max-w-[60%] whitespace-pre-wrap break-words text-right text-[13px] text-ink">
                    {confirming.request.reason ?? "Not given"}
                  </dd>
                </div>
              </dl>
              <p className="mt-3 text-xs leading-relaxed text-muted">
                This decision is final. Once recorded it cannot be changed from this screen.
              </p>
            </DialogBody>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConfirming(null)} disabled={decide.isPending}>
                Cancel
              </Button>
              <Button
                variant={confirming.status === "approved" ? "primary" : "danger"}
                onClick={onDecide}
                loading={decide.isPending}
              >
                {confirming.status === "approved" ? "Approve leave" : "Decline leave"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {logging && <LogLeaveDialog members={members} onClose={() => setLogging(false)} />}

      {editingBalancesFor !== null && (
        <EditBalancesDialog
          /* Looked up on every render rather than captured when the button was
             clicked, so the "now" figures follow the query after a save. */
          member={members.find((m) => m.id === editingBalancesFor) ?? null}
          onClose={() => setEditingBalancesFor(null)}
        />
      )}

      {viewing && <LeaveDetailDialog request={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

/** A tab name with a count beside it, shown only when there is something to count. */
function TabLabel({ count, children }: { count: number; children: ReactNode }) {
  return (
    <span className="flex items-center gap-1.5 whitespace-nowrap">
      {children}
      {count > 0 && (
        <span className="rounded-full bg-warn-wash px-1.5 text-[11px] font-semibold leading-[18px] tabular-nums text-warn">
          {count}
          <span className="sr-only"> waiting</span>
        </span>
      )}
    </span>
  );
}

/** The filter row under a panel header. Wraps rather than widening the page. */
function Toolbar({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2.5">{children}</div>
  );
}

/** A compact search box for panel toolbars. */
function SearchBox({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  label: string;
}) {
  return (
    <div className="relative w-full sm:w-auto">
      <Icon
        name="search"
        size={16}
        className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-faint"
      />
      <Input
        type="search"
        aria-label={label}
        placeholder="Search by name"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 w-full pl-8 text-[13px] sm:w-44"
      />
    </div>
  );
}

function TeamFilter({
  teams,
  value,
  onChange,
}: {
  teams: string[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <Select
      aria-label="Filter by team"
      className="h-8 w-auto text-[13px]"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="all">All teams</option>
      {teams.map((team) => (
        <option key={team} value={team}>
          {team}
        </option>
      ))}
    </Select>
  );
}

function DecideButtons({ request, onDecide }: { request: LeaveRequest; onDecide: (c: Confirming) => void }) {
  return (
    <span className="flex items-center gap-1.5">
      {/* Rows open the detail dialog, so these stop the click going through —
          a decide button that also opened a dialog would fire both. */}
      <Button
        variant="primary"
        size="sm"
        onClick={(event) => {
          event.stopPropagation();
          onDecide({ request, status: "approved" });
        }}
      >
        Approve
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={(event) => {
          event.stopPropagation();
          onDecide({ request, status: "rejected" });
        }}
      >
        Decline
      </Button>
    </span>
  );
}

/* ------------------------------------------------------------ Requests tab */

type QueueItem =
  | { kind: "leave"; request: LeaveRequest }
  | { kind: "wfh"; request: WfhRequest };

/** Tells leave and work from home apart at a glance — one is a day off, the
 *  other a day worked, and they must not read as the same thing. */
function KindTag({ kind }: { kind: QueueItem["kind"] }) {
  return kind === "leave" ? (
    <Badge tone="accent" icon="leave">
      Leave
    </Badge>
  ) : (
    <Badge tone="outline" icon="wfh">
      WFH
    </Badge>
  );
}

function DecisionQueue({
  loading,
  requests,
  wfhRequests,
  onDecide,
  onDecideWfh,
  onView,
}: {
  loading: boolean;
  requests: LeaveRequest[];
  wfhRequests: WfhRequest[];
  onDecide: (c: Confirming) => void;
  onDecideWfh: (c: WfhConfirming) => void;
  onView: (r: LeaveRequest) => void;
}) {
  // Soonest first: the request whose day is closest is the one to decide next.
  const items: QueueItem[] = [
    ...requests.map((request) => ({ kind: "leave" as const, request })),
    ...wfhRequests.map((request) => ({ kind: "wfh" as const, request })),
  ].sort((a, b) => a.request.start_date.localeCompare(b.request.start_date));

  return (
    <Panel>
      <PanelHeader
        title={
          <span className="flex items-center gap-2">
            Waiting on you
            {items.length > 0 && <Badge tone="warn">{items.length}</Badge>}
          </span>
        }
        note="Leave from managers and HR & Admin, and anyone's work from home. Soonest first."
      />
      {loading ? (
        <SkeletonTable rows={3} cols={3} />
      ) : items.length === 0 ? (
        <div className="flex items-center gap-3 px-4 py-6">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-good-wash text-good">
            <Icon name="check-circle" size={20} />
          </span>
          <div>
            <p className="text-sm font-medium text-ink">You're all caught up</p>
            <p className="text-xs text-muted">Nothing is waiting on a decision from HR right now.</p>
          </div>
        </div>
      ) : (
        <ul className="divide-y divide-line">
          {items.map((item) => {
            const request = item.request;
            const body = (
              <>
                <Avatar name={request.employee_name} size="sm" />
                <span className="min-w-0">
                  <span className="flex flex-wrap items-center gap-1.5">
                    <span className="truncate text-sm font-medium text-ink">
                      {request.employee_name ?? "Unnamed"}
                    </span>
                    <KindTag kind={item.kind} />
                    <FiledLateBadge request={request} />
                  </span>
                  <span className="block truncate text-xs text-muted">
                    {item.kind === "leave" ? `${leaveLabel(item.request.leave_type)} · ` : ""}
                    {dateSpan(request.start_date, request.end_date)} · {formatDays(request.days)}{" "}
                    {request.days === 1 ? "day" : "days"} · {teamOf(request)}
                  </span>
                  {request.reason && (
                    <span className="mt-0.5 block max-w-[60ch] truncate text-xs text-faint">
                      “{request.reason}”
                    </span>
                  )}
                </span>
              </>
            );
            return (
              <li
                key={`${item.kind}-${request.id}`}
                className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3"
              >
                {item.kind === "leave" ? (
                  <button
                    type="button"
                    onClick={() => onView(item.request)}
                    className="flex min-w-0 flex-1 items-center gap-3 rounded text-left"
                  >
                    {body}
                  </button>
                ) : (
                  <div className="flex min-w-0 flex-1 items-center gap-3">{body}</div>
                )}
                {item.kind === "leave" ? (
                  <DecideButtons request={item.request} onDecide={onDecide} />
                ) : (
                  <span className="flex items-center gap-1.5">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => onDecideWfh({ request: item.request, status: "approved" })}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDecideWfh({ request: item.request, status: "rejected" })}
                    >
                      Decline
                    </Button>
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

function AwayRow({
  request,
  when,
  onView,
}: {
  request: LeaveRequest;
  when: string;
  onView: (r: LeaveRequest) => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={() => onView(request)}
        className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-surface-sunken/60"
      >
        <Avatar name={request.employee_name} size="sm" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-ink">
            {request.employee_name ?? "Unnamed"}
          </span>
          <span className="block truncate text-xs text-muted">
            {LEAVE_TYPE_SHORT[request.leave_type] ?? request.leave_type} · {teamOf(request)}
          </span>
        </span>
        <span className="shrink-0 font-mono text-xs text-muted">{when}</span>
      </button>
    </li>
  );
}

/**
 * Approved leave covering today, and approved leave starting within the week —
 * the "who can I not reach" question HR gets asked most. A day handed back by a
 * withdrawal is not a day away, so it doesn't count someone as out today.
 */
function AwayPanel({
  loading,
  requests,
  onView,
}: {
  loading: boolean;
  requests: LeaveRequest[];
  onView: (r: LeaveRequest) => void;
}) {
  const today = calendarTodayStr();
  const horizon = shiftDate(today, AWAY_LOOKAHEAD_DAYS);

  const { now, soon } = useMemo(() => {
    const approved = requests.filter((r) => r.status === "approved");
    const now = approved
      .filter(
        (r) => r.start_date <= today && r.end_date >= today && !(r.withdrawn_days ?? []).includes(today),
      )
      .sort((a, b) => a.end_date.localeCompare(b.end_date));
    const soon = approved
      .filter((r) => r.start_date > today && r.start_date <= horizon)
      .sort((a, b) => a.start_date.localeCompare(b.start_date));
    return { now, soon };
  }, [requests, today, horizon]);

  return (
    <Panel>
      <PanelHeader title="Who's away" note={`Approved leave, today and the next ${AWAY_LOOKAHEAD_DAYS} days`} />
      {loading ? (
        <SkeletonTable rows={3} cols={2} />
      ) : now.length === 0 && soon.length === 0 ? (
        <p className="px-4 py-6 text-sm text-muted">Nobody is on approved leave this week.</p>
      ) : (
        <div className="divide-y divide-line">
          <section>
            <h3 className="px-4 pb-1 pt-3 text-xs font-medium text-muted">
              Today · {now.length}
            </h3>
            {now.length === 0 ? (
              <p className="px-4 pb-3 text-xs text-faint">Everyone is in.</p>
            ) : (
              <ul className="pb-1">
                {now.map((r) => (
                  <AwayRow
                    key={r.id}
                    request={r}
                    when={r.end_date === today ? "Last day today" : `Until ${formatDate(r.end_date, "d MMM")}`}
                    onView={onView}
                  />
                ))}
              </ul>
            )}
          </section>
          {soon.length > 0 && (
            <section>
              <h3 className="px-4 pb-1 pt-3 text-xs font-medium text-muted">Coming up · {soon.length}</h3>
              <ul className="pb-1">
                {soon.map((r) => (
                  <AwayRow key={r.id} request={r} when={dateSpan(r.start_date, r.end_date)} onView={onView} />
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </Panel>
  );
}

const STATUS_FILTERS: { value: LeaveStatus | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Declined" },
  { value: "withdrawn", label: "Withdrawn" },
];

/**
 * ‹ October 2026 › with an "All months" switch beside it. Stepping the month
 * while "All months" is on goes back to a single month, starting from this one.
 */
function MonthScopeControl({ month, onChange }: { month: MonthScope; onChange: (month: MonthScope) => void }) {
  const all = month === ALL_MONTHS;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {all ? (
        <Button variant="ghost" size="sm" iconLeft="calendar" onClick={() => onChange(currentMonth())}>
          Back to one month
        </Button>
      ) : (
        <MonthStepper month={month} onChange={onChange} />
      )}
      <label className="flex h-8 items-center gap-2 whitespace-nowrap text-[13px] text-ink-soft">
        <input
          type="checkbox"
          checked={all}
          onChange={(e) => onChange(e.target.checked ? ALL_MONTHS : currentMonth())}
        />
        All months
      </label>
    </div>
  );
}

function scopeLabel(month: MonthScope): string {
  return month === ALL_MONTHS ? "every month on record" : (formatDate(`${month}-01`, "MMMM yyyy") ?? month);
}

function RequestLog({
  loading,
  requests: allRequests,
  month,
  onMonthChange,
  currentUser,
  onDecide,
  onView,
}: {
  loading: boolean;
  requests: LeaveRequest[];
  month: MonthScope;
  onMonthChange: (month: MonthScope) => void;
  currentUser: SessionUser | null;
  onDecide: (c: Confirming) => void;
  onView: (r: LeaveRequest) => void;
}) {
  const requests = useMemo(
    () => allRequests.filter((r) => inScope(r, month)).sort(byPendingThenNewest),
    [allRequests, month],
  );
  const [status, setStatus] = useState<LeaveStatus | "all">("all");
  const [team, setTeam] = useState("all");
  const [type, setType] = useState<LeaveType | "all">("all");
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(REQUEST_PAGE_SIZE);

  const teams = useMemo(() => [...new Set(requests.map(teamOf))].sort(), [requests]);
  const types = useMemo(
    () => MONTHLY_SUMMARY_TYPE_ORDER.filter((t) => requests.some((r) => r.leave_type === t)),
    [requests],
  );

  // Everything but the status filter, so each status chip can say how many it holds.
  const scoped = useMemo(
    () =>
      requests.filter(
        (r) =>
          (team === "all" || teamOf(r) === team) &&
          (type === "all" || r.leave_type === type) &&
          matchesSearch(r.employee_name, query),
      ),
    [requests, team, type, query],
  );
  const filtered = status === "all" ? scoped : scoped.filter((r) => r.status === status);
  const countFor = (value: LeaveStatus | "all") =>
    value === "all" ? scoped.length : scoped.filter((r) => r.status === value).length;
  const hasFilters = status !== "all" || team !== "all" || type !== "all" || query.trim() !== "";

  function resetFilters() {
    setStatus("all");
    setTeam("all");
    setType("all");
    setQuery("");
  }

  return (
    <Panel>
      <PanelHeader
        title="Leave requests"
        note={`Leave with any day in ${scopeLabel(month)}. Pending first, then latest. Click a row for the full detail.`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <MonthScopeControl month={month} onChange={onMonthChange} />
            {hasFilters && (
              <Button variant="ghost" size="sm" iconLeft="close" onClick={resetFilters}>
                Clear filters
              </Button>
            )}
          </div>
        }
      />
      <Toolbar>
        <Tabs value={status} onValueChange={(v) => setStatus(v as LeaveStatus | "all")}>
          <TabsList aria-label="Filter by status" className="flex-wrap">
            {STATUS_FILTERS.map((f) => (
              <TabsTrigger key={f.value} value={f.value}>
                {f.label}
                <span className="ml-1.5 tabular-nums text-faint">{loading ? "" : countFor(f.value)}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
          <SearchBox value={query} onChange={setQuery} label="Search requests by name" />
          <TeamFilter teams={teams} value={team} onChange={setTeam} />
          <Select
            aria-label="Filter by leave type"
            className="h-8 w-auto text-[13px]"
            value={type}
            onChange={(e) => setType(e.target.value as LeaveType | "all")}
          >
            <option value="all">All types</option>
            {types.map((t) => (
              <option key={t} value={t}>
                {leaveLabel(t)}
              </option>
            ))}
          </Select>
        </div>
      </Toolbar>
      {loading ? (
        <SkeletonTable rows={6} cols={7} />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="leave"
          title={
            requests.length === 0
              ? month === ALL_MONTHS
                ? "No leave requests yet"
                : `No leave in ${scopeLabel(month)}`
              : "Nothing matches these filters"
          }
          body={
            requests.length === 0
              ? month === ALL_MONTHS
                ? "Requests appear here as soon as anyone applies for leave."
                : "Step back a month with the arrows, or tick All months."
              : "Try a different name, status, team or leave type."
          }
          action={
            hasFilters ? (
              <Button variant="secondary" size="sm" onClick={resetFilters}>
                Clear filters
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <TableWrap>
            <THead>
              <TH>Person</TH>
              <TH>Type</TH>
              <TH>Dates</TH>
              <TH numeric>Days</TH>
              <TH>Reason</TH>
              <TH>Status</TH>
              <TH>
                <span className="sr-only">Decision</span>
              </TH>
            </THead>
            <TBody>
              {filtered.slice(0, limit).map((request) => (
                <TR key={request.id} onClick={() => onView(request)}>
                  <TDPrimary>
                    <span className="flex items-center gap-2">
                      <Avatar name={request.employee_name} size="sm" />
                      <span className="min-w-0">
                        <span className="block truncate">{request.employee_name ?? "Unnamed"}</span>
                        <span className="block truncate text-xs font-normal text-muted">{teamOf(request)}</span>
                      </span>
                    </span>
                  </TDPrimary>
                  <TD className="whitespace-nowrap">{leaveLabel(request.leave_type)}</TD>
                  <TD className="whitespace-nowrap font-mono text-xs">
                    {dateSpan(request.start_date, request.end_date)}
                  </TD>
                  <TD numeric>{formatDays(request.days)}</TD>
                  <TD className="max-w-[26ch]">
                    <ReasonCell reason={request.reason} onOpen={() => onView(request)} />
                  </TD>
                  <TD>
                    <span className="flex flex-wrap items-center gap-1.5">
                      <StatusBadge status={request.status} />
                      <FiledLateBadge request={request} />
                    </span>
                  </TD>
                  <TD>
                    {currentUser && canDecide(request, currentUser) ? (
                      <DecideButtons request={request} onDecide={onDecide} />
                    ) : request.status === "pending" ? (
                      <span className="whitespace-nowrap text-xs text-muted">
                        {waitingOnLabel(request, currentUser?.id)}
                      </span>
                    ) : (
                      <span className="whitespace-nowrap text-xs text-faint">
                        {request.decided_at
                          ? `${request.decided_by_name ?? "Decided"} · ${formatTimestampDate(request.decided_at, "d MMM")}`
                          : "—"}
                      </span>
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </TableWrap>
          <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-2.5 text-xs text-muted">
            <span>
              Showing {Math.min(limit, filtered.length)} of {filtered.length}
            </span>
            {filtered.length > limit && (
              <Button variant="ghost" size="sm" onClick={() => setLimit((l) => l + REQUEST_PAGE_SIZE)}>
                Show {Math.min(REQUEST_PAGE_SIZE, filtered.length - limit)} more
              </Button>
            )}
          </div>
        </>
      )}
    </Panel>
  );
}

/**
 * Work from home, kept below leave and smaller than it: the pending ones are
 * already in "Waiting on you", so this is the record — the same month as the
 * leave list above — and the weekly pattern.
 */
function WfhSection({
  loading,
  error,
  onRetry,
  requests,
  month,
  currentUserId,
  onDecide,
}: {
  loading: boolean;
  error: Error | null;
  onRetry: () => void;
  requests: WfhRequest[];
  month: MonthScope;
  currentUserId: number | undefined;
  onDecide: (c: WfhConfirming) => void;
}) {
  const shown = useMemo(
    () => requests.filter((r) => inScope(r, month)).sort(byPendingThenNewest),
    [requests, month],
  );

  return (
    <section className="flex flex-col gap-4" aria-labelledby="hr-wfh-heading">
      <div className="flex items-center gap-2 pt-2">
        <Icon name="wfh" size={16} className="text-muted" />
        <h2 id="hr-wfh-heading" className="text-sm font-semibold text-ink">
          Work from home
        </h2>
        <span className="text-xs text-muted">A day worked from home, not a day off — no leave is used.</span>
      </div>
      <WfhWeekCharts scopeLabel="the organisation" />
      <Panel>
        <PanelHeader
          title="Work-from-home requests"
          note={`Requests with any day in ${scopeLabel(month)}. Month follows the leave list above.`}
        />
        {error ? (
          <ErrorState message={error.message} onRetry={onRetry} />
        ) : (
          <WfhRequestTable
            requests={shown}
            loading={loading}
            showTeam
            currentUserId={currentUserId}
            emptyTitle={month === ALL_MONTHS ? "No requests yet" : `No work from home in ${scopeLabel(month)}`}
            emptyBody="Requests appear here as soon as somebody asks to work from home."
            onDecide={(request, status) => onDecide({ request, status })}
          />
        )}
      </Panel>
    </section>
  );
}

/* ------------------------------------------------------------ Balances tab */

/** The columns the balances table shows — Vyomi's pick, 7 Oct 2026. Menstrual
 *  Leave is left out of the table and shown in the person's detail instead. */
const BALANCE_COLUMNS: LeaveType[] = ["EL", "CL", "SL", "OptionalHoliday", "CompOff", "Special", "LWP"];

/**
 * The days taken that go with a line, as one number:
 *   - Comp Off: days used.
 *   - HR-set with a year's total: taken this year (total − left).
 *   - HR-set without one: taken since HR set it — the only figure on record.
 *   - Policy lines, Special, LWP: taken this year.
 */
function takenFor(type: LeaveType, line: LeaveBalanceLine): number {
  if (type === "CompOff") return line.taken;
  if (line.setByHr && line.yearTotal == null) return line.takenSince ?? line.taken;
  return line.takenThisYear ?? line.taken;
}

/** What the taken figure counts from, in words — for the detail dialog. */
function takenSinceLabel(type: LeaveType, line: LeaveBalanceLine): string {
  if (type === "CompOff") return "Used";
  if (line.setByHr && line.yearTotal == null) {
    return `Taken since ${formatTimestampDate(line.setByHr.setAt, "d MMM")}`;
  }
  return "Taken this year";
}

/**
 * The "out of" figure for a cell, so that every cell reads like Comp Off does —
 * used / what there was, with what is left underneath:
 *   - A year's total HR stated, when there is one.
 *   - Comp Off: days earned.
 *   - HR-set without a year's total: the number HR typed (`entitlement` on
 *     that line), which is exactly what was taken since plus what is left.
 *   - Policy lines: the policy's annual amount (EL 12, CL 6, …).
 */
function yearTotalFor(line: LeaveBalanceLine): number {
  if (line.yearTotal != null) return line.yearTotal;
  return line.entitlement ?? 0;
}

/**
 * One balance as a table cell, the way Vyomi asked for it (7 Oct 2026):
 * "taken / year's total" as the main figure, days left small underneath. An
 * HR-set line with no year's total yet reads "1 / —" rather than a guessed
 * total. Special and LWP have no total, so they show days taken, or a dash when
 * there are none. The dot marks a "left" figure HR typed by hand.
 */
function BalanceCell({ type, line }: { type: LeaveType; line: LeaveBalanceLine | undefined }) {
  if (!line) {
    return (
      <span className="text-faint">
        —<span className="sr-only">Not eligible</span>
      </span>
    );
  }
  const taken = takenFor(type, line);
  const setBy = line.setByHr
    ? `Set by ${line.setByHr.setByName ?? "HR"} on ${formatTimestampDate(line.setByHr.setAt, "d MMM yyyy")}`
    : undefined;

  if (line.available === null) {
    return taken > 0 ? (
      <span className="inline-flex flex-col items-end whitespace-nowrap">
        <span className="text-[15px] font-semibold text-ink">{formatDays(taken)}</span>
        <span className="text-[11px] leading-tight text-muted">taken</span>
      </span>
    ) : (
      <span className="text-faint">—</span>
    );
  }

  const total = yearTotalFor(line);

  return (
    <span className="inline-flex flex-col items-end gap-0.5 whitespace-nowrap" title={setBy}>
      <span className="inline-flex items-center gap-1.5">
        {setBy && <span className="h-1.5 w-1.5 rounded-full bg-accent" aria-hidden />}
        <span className="text-[15px] font-semibold tabular-nums text-ink">{formatDays(taken)}</span>
        <span className="text-[13px] tabular-nums text-muted">/ {formatDays(total)}</span>
      </span>
      <span
        className={cn(
          "text-[11px] leading-tight tabular-nums",
          line.available < 0 ? "font-medium text-bad" : "text-muted",
        )}
      >
        {formatDays(line.available)} left
      </span>
      <span className="sr-only">
        {` ${formatDays(taken)} ${type === "CompOff" ? "used" : "taken"} of ${formatDays(total)}, ${formatDays(line.available)} left`}
        {setBy ? ` · ${setBy}` : ""}
      </span>
    </span>
  );
}

/** A column earns its place if anybody in view has something in it. */
function columnHasContent(members: LeaveSummaryMember[], type: LeaveType): boolean {
  return members.some((m) => {
    const line = m.balance[type];
    return (
      line !== undefined &&
      ((line.entitlement ?? 0) > 0 || line.taken > 0 || (line.available ?? 0) > 0 || !!line.setByHr)
    );
  });
}

/**
 * Everything about one person's leave, every type including the ones the table
 * leaves out: left, taken (and what it counts from), the year's total, and who
 * set a figure by hand.
 */
function PersonBalanceDialog({
  member,
  onClose,
  onEdit,
}: {
  member: LeaveSummaryMember;
  onClose: () => void;
  onEdit: () => void;
}) {
  const types = MONTHLY_SUMMARY_TYPE_ORDER.filter((t) => member.balance[t] !== undefined);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader
          title={member.name ?? "Unnamed"}
          description={`${teamOf(member)} · ${EMPLOYMENT_TYPE_LABEL[member.employment_type]}`}
        />
        <DialogBody className="flex flex-col gap-3">
          {types.map((type) => {
            const line = member.balance[type] as LeaveBalanceLine;
            const rows: [string, string][] = [];
            if (line.available !== null) rows.push(["Left", formatDays(line.available)]);
            rows.push([takenSinceLabel(type, line), formatDays(takenFor(type, line))]);
            if (type === "CompOff") rows.push(["Earned", formatDays(line.entitlement ?? 0)]);
            if (line.setByHr) {
              rows.push(["Year's total", line.yearTotal != null ? formatDays(line.yearTotal) : "Not entered yet"]);
              rows.push([
                "Left figure set by",
                `${line.setByHr.setByName ?? "HR"}, ${formatTimestampDate(line.setByHr.setAt, "d MMM yyyy")}`,
              ]);
              if (line.setByHr.note) rows.push(["Note", line.setByHr.note]);
            }
            return (
              <section key={type} className="rounded-lg border border-line">
                <h3 className="border-b border-line px-3 py-2 text-[13px] font-medium text-ink">
                  {leaveLabel(type)}
                </h3>
                <dl className="divide-y divide-line">
                  {rows.map(([label, value]) => (
                    <div key={label} className="flex justify-between gap-4 px-3 py-1.5">
                      <dt className="text-[13px] text-muted">{label}</dt>
                      <dd
                        className={cn(
                          "text-right text-[13px] text-ink",
                          value === "Not entered yet" && "text-warn",
                        )}
                      >
                        {value}
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>
            );
          })}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button variant="primary" iconLeft="edit" onClick={onEdit}>
            Edit balances
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/*
  One team at a time. The whole org as one grid was eight columns by everyone,
  most of it zeros; a team is a handful of people HR can actually read. The
  chosen team lives in the URL alongside the tab.
*/
function BalancesPanel({
  loading,
  error,
  onRetry,
  members,
  year,
  onEdit,
}: {
  loading: boolean;
  error: Error | null;
  onRetry: () => void;
  members: LeaveSummaryMember[];
  year: number;
  onEdit: (id: number) => void;
}) {
  const [params, setParams] = useSearchParams();
  const teams = useMemo(() => {
    const counts = new Map<string, number>();
    for (const m of members) counts.set(teamOf(m), (counts.get(teamOf(m)) ?? 0) + 1);
    // "Unassigned" isn't a team, so it goes last rather than wherever U sorts.
    return [...counts.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) =>
        a.name === "Unassigned" ? 1 : b.name === "Unassigned" ? -1 : a.name.localeCompare(b.name),
      );
  }, [members]);

  const requested = params.get("team");
  const team = teams.find((t) => t.name === requested)?.name ?? teams[0]?.name ?? null;

  function selectTeam(name: string) {
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        out.set("team", name);
        return out;
      },
      { replace: true },
    );
  }

  const people = members
    .filter((m) => teamOf(m) === team)
    .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
  const types = BALANCE_COLUMNS.filter((t) => columnHasContent(people, t));
  const [detailFor, setDetailFor] = useState<number | null>(null);
  const detailMember = members.find((m) => m.id === detailFor) ?? null;

  return (
    <Panel>
      <PanelHeader
        title={`Leave balances · ${year}`}
        note={
          <>
            Taken / total, with days left underneath — the same way Comp Off reads used / earned. Where HR
            typed the balance and no year&rsquo;s total is entered, the total is HR&rsquo;s number and
            &ldquo;taken&rdquo; counts from that day.{" "}
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent align-middle" /> = figure typed in by
            HR. Click a name for the full detail.
          </>
        }
      />
      {error ? (
        <ErrorState message={error.message} onRetry={onRetry} />
      ) : loading ? (
        <SkeletonTable rows={6} cols={5} />
      ) : teams.length === 0 || team === null ? (
        <EmptyState icon="team" title="No accounts to show" />
      ) : (
        <>
          <div className="border-b border-line px-4 py-3">
            <Tabs value={team} onValueChange={selectTeam}>
              <TabsList aria-label="Team" className="flex-wrap [&>*]:whitespace-nowrap">
                {teams.map((t) => (
                  <TabsTrigger key={t.name} value={t.name}>
                    {t.name}
                    <span className="ml-1.5 tabular-nums text-faint">{t.count}</span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
          <TableWrap>
            <THead>
              <TH className={STICKY_HEAD}>Person</TH>
              {types.map((t) => (
                <TH key={t} numeric>
                  {leaveLabel(t)}
                </TH>
              ))}
              <TH>
                <span className="sr-only">Edit</span>
              </TH>
            </THead>
            <TBody>
              {people.map((member) => (
                <TR key={member.id}>
                  <TDPrimary className={STICKY_CELL}>
                    <button
                      type="button"
                      onClick={() => setDetailFor(member.id)}
                      className="flex items-center gap-2 rounded text-left hover:underline"
                    >
                      <Avatar name={member.name} size="sm" />
                      <span className="min-w-0">
                        <span className="block truncate">{member.name ?? "Unnamed"}</span>
                        <span className="block truncate text-xs font-normal text-muted">
                          {EMPLOYMENT_TYPE_LABEL[member.employment_type]}
                        </span>
                      </span>
                    </button>
                  </TDPrimary>
                  {types.map((t) => (
                    <TD key={t} numeric className="align-top">
                      <BalanceCell type={t} line={member.balance[t]} />
                    </TD>
                  ))}
                  <TD className="w-0 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconLeft="edit"
                      aria-label={`Edit balances for ${member.name ?? "this person"}`}
                      onClick={() => onEdit(member.id)}
                    >
                      Edit
                    </Button>
                  </TD>
                </TR>
              ))}
            </TBody>
          </TableWrap>
        </>
      )}
      {detailMember && (
        <PersonBalanceDialog
          member={detailMember}
          onClose={() => setDetailFor(null)}
          onEdit={() => {
            setDetailFor(null);
            onEdit(detailMember.id);
          }}
        />
      )}
    </Panel>
  );
}

/* ------------------------------------------------------- Monthly summary tab */

function MonthlySummaryPanel() {
  const [month, setMonth] = useState(currentMonth());
  const monthly = useAdminLeaveMonthlySummary(month);
  const [query, setQuery] = useState("");
  const [team, setTeam] = useState("all");
  const [hideEmpty, setHideEmpty] = useState(true);

  const members = monthly.data?.members ?? [];
  const types = MONTHLY_SUMMARY_TYPE_ORDER.filter((type) => members.some((m) => m.byType[type] !== undefined));
  const teams = useMemo(() => [...new Set(members.map(teamOf))].sort(), [members]);
  // Leave days only. An unrecorded day is not leave anybody took — it is a gap
  // in the record — so it is counted beside the total, never inside it.
  const totalFor = (m: (typeof members)[number]) => types.reduce((sum, t) => sum + (m.byType[t] ?? 0), 0);

  const scoped = members.filter((m) => matchesSearch(m.name, query) && (team === "all" || teamOf(m) === team));
  const shown = hideEmpty ? scoped.filter((m) => totalFor(m) > 0 || m.lwp > 0) : scoped;
  const hiddenCount = scoped.length - shown.length;

  const columnTotal = (pick: (m: (typeof members)[number]) => number) =>
    shown.reduce((sum, m) => sum + pick(m), 0);

  const num = (value: number) => (
    <span className={value === 0 ? "text-faint" : undefined}>{formatDays(value)}</span>
  );

  return (
    <Panel>
      <PanelHeader
        title="Monthly leave summary"
        note={`Leave days taken in the month, by type. Total is leave only. ${UNRECORDED_ABSENCE_LABEL}, kept apart on the right, is a past working day with no attendance and no approved leave — a gap to look into, not leave.`}
        actions={<MonthStepper month={month} onChange={setMonth} />}
      />
      <Toolbar>
        <SearchBox value={query} onChange={setQuery} label="Search the monthly summary by name" />
        <TeamFilter teams={teams} value={team} onChange={setTeam} />
        <label className="flex h-8 items-center gap-2 whitespace-nowrap text-[13px] text-ink-soft">
          <input type="checkbox" checked={hideEmpty} onChange={(e) => setHideEmpty(e.target.checked)} />
          Hide people with nothing this month
        </label>
        {hideEmpty && hiddenCount > 0 && !monthly.isLoading && (
          <span className="text-xs text-muted">{hiddenCount} hidden</span>
        )}
      </Toolbar>
      {monthly.error ? (
        <ErrorState message={monthly.error.message} onRetry={() => void monthly.refetch()} />
      ) : monthly.isLoading ? (
        <SkeletonTable rows={6} cols={5} />
      ) : shown.length === 0 ? (
        <EmptyState
          icon="calendar-check"
          title={members.length === 0 ? "No accounts to show" : "No leave to show for this month"}
          body={
            hideEmpty && hiddenCount > 0
              ? "Nobody here took leave or had an unrecorded day. Untick the box to see everyone."
              : undefined
          }
        />
      ) : (
        <TableWrap>
          <THead>
            <TH className={STICKY_HEAD}>Person</TH>
            {types.map((type) => (
              <TH key={type} numeric>
                {leaveLabel(type)}
              </TH>
            ))}
            <TH numeric>Total leave</TH>
            <TH numeric className={UNRECORDED_COL}>
              {UNRECORDED_ABSENCE_LABEL} days
            </TH>
          </THead>
          <TBody>
            {shown.map((member) => (
              <TR key={member.id}>
                <TDPrimary className={STICKY_CELL}>
                  <span className="flex items-center gap-2">
                    <Avatar name={member.name} size="sm" />
                    <span className="min-w-0">
                      <span className="block truncate">{member.name ?? "Unnamed"}</span>
                      <span className="block truncate text-xs font-normal text-muted">{teamOf(member)}</span>
                    </span>
                  </span>
                </TDPrimary>
                {types.map((type) => (
                  <TD key={type} numeric>
                    {num(member.byType[type] ?? 0)}
                  </TD>
                ))}
                <TD numeric className="font-medium text-ink">
                  {num(totalFor(member))}
                </TD>
                <TD numeric className={cn(UNRECORDED_COL, member.lwp > 0 && "font-medium text-bad")}>
                  {num(member.lwp)}
                </TD>
              </TR>
            ))}
          </TBody>
          <tfoot className="border-t border-line-strong bg-surface-sunken">
            <tr>
              <td className={cn(STICKY_HEAD, "border-b-0 px-3 py-2.5 text-xs font-medium text-muted")}>
                Total · {shown.length} {shown.length === 1 ? "person" : "people"}
              </td>
              {types.map((type) => (
                <td key={type} className="px-3 py-2.5 text-right font-medium tabular-nums text-ink">
                  {formatDays(columnTotal((m) => m.byType[type] ?? 0))}
                </td>
              ))}
              <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-ink">
                {formatDays(columnTotal(totalFor))}
              </td>
              <td className={cn(UNRECORDED_COL, "px-3 py-2.5 text-right font-medium tabular-nums text-ink")}>
                {formatDays(columnTotal((m) => m.lwp))}
              </td>
            </tr>
          </tfoot>
        </TableWrap>
      )}
    </Panel>
  );
}

/* ------------------------------------------------------------- Insights tab */

/** How many people the "most leave left" list shows. */
const MOST_LEFT_COUNT = 10;

/*
  Four views, every one of them this calendar year and every one in approved
  DAYS, so any two can be held side by side and agree. It used to be four tiles,
  four charts and a table, three of which were leave-by-type counted three
  different ways (request counts, every year on record, and balance lines that
  mixed lifetime EL with leave since HR set a figure).
*/
function InsightsView({
  loading,
  summaryLoading,
  requests,
  members,
  year,
}: {
  loading: boolean;
  summaryLoading: boolean;
  requests: LeaveRequest[];
  members: LeaveSummaryMember[];
  year: number;
}) {
  const approvedDays = approvedDaysInYear(requests, year);
  const byMonth = useMemo(() => leaveDaysByMonth(requests, year), [requests, year]);
  const byTeam = useMemo(() => approvedDaysBy(requests, teamOf, year), [requests, year]);
  const byType = useMemo(() => approvedDaysBy(requests, (r) => leaveLabel(r.leave_type), year), [requests, year]);
  const mostLeft = useMemo(
    () =>
      members
        .filter((m) => typeof m.balance.EL?.available === "number")
        .map((m) => ({ member: m, line: m.balance.EL as LeaveBalanceLine }))
        .sort(
          (a, b) =>
            (b.line.available ?? 0) - (a.line.available ?? 0) ||
            (a.member.name ?? "").localeCompare(b.member.name ?? ""),
        )
        .slice(0, MOST_LEFT_COUNT),
    [members],
  );

  return (
    <div className="flex flex-col gap-6">
      <p className="text-[13px] text-muted">
        Calendar year {year}, approved leave only, counted in working days.{" "}
        {!loading && (
          <span className="text-ink-soft">
            {formatDays(approvedDays)} {approvedDays === 1 ? "day" : "days"} approved so far.
          </span>
        )}
      </p>

      <div className="grid gap-4 xl:grid-cols-2">
        <TrendChart
          title="Leave days by month"
          note={`Approved days, org-wide, ${year}`}
          data={byMonth}
          xKey="month"
          series={[{ key: "days", label: "Days approved" }]}
          area
          loading={loading}
          emptyBody="No leave has been approved this year yet."
        />
        <CategoryBarChart
          title="Leave days by team"
          note={`Approved days, ${year}`}
          data={byTeam}
          valueHeader="Days"
          loading={loading}
          height={Math.max(200, byTeam.length * 36)}
          emptyBody="No leave has been approved this year yet."
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <CategoryBarChart
          title="Leave days by type"
          note={`Approved days, ${year}`}
          data={byType}
          valueHeader="Days"
          loading={loading}
          height={Math.max(200, byType.length * 36)}
          emptyBody="No leave has been approved this year yet."
        />
        <Panel>
          <PanelHeader
            title="Most Earned Leave left"
            note={`The ${MOST_LEFT_COUNT} people with the most Earned Leave still to take. It carries forward, up to 40 days.`}
          />
          {summaryLoading ? (
            <SkeletonTable rows={5} cols={3} />
          ) : mostLeft.length === 0 ? (
            <EmptyState icon="leave" title="Nobody has Earned Leave on file" />
          ) : (
            <TableWrap>
              <THead>
                <TH>Person</TH>
                <TH>Team</TH>
                <TH numeric>Earned Leave left</TH>
              </THead>
              <TBody>
                {mostLeft.map(({ member, line }) => (
                  <TR key={member.id}>
                    <TDPrimary>
                      <span className="flex items-center gap-2">
                        <Avatar name={member.name} size="sm" />
                        <span className="truncate">{member.name ?? "Unnamed"}</span>
                      </span>
                    </TDPrimary>
                    <TD className="whitespace-nowrap">{teamOf(member)}</TD>
                    <TD numeric className="font-medium text-ink">
                      {formatDays(line.available)}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </TableWrap>
          )}
        </Panel>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Dialogs */

function LogLeaveDialog({
  members,
  onClose,
}: {
  members: LeaveSummaryMember[];
  onClose: () => void;
}) {
  const log = useLogLeaveForUser();
  /*
    Empty until somebody chooses. It used to seed to the first member, which the
    browser then painted as selected while the list was still loading and the
    state was still "" — a name on screen, and "Pick who this leave is for." on
    save. The placeholder option below keeps what is shown and what is held the
    same value at every moment.
  */
  const [userId, setUserId] = useState<string>("");
  const [leaveType, setLeaveType] = useState<LeaveType | "">("");
  const [startDate, setStartDate] = useState(todayStr());
  const [endDate, setEndDate] = useState(todayStr());
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  /*
    Which types this PERSON can be given, straight from the server's own answer:
    summarizeByType builds each member's balance from eligibleLeaveTypesFor, so
    the keys of that balance are the eligibility rule, already applied. Listing
    every type that exists instead meant offering Earned Leave for an intern and
    finding out only when the backend rejected the save.

    Before a person is chosen there is nobody to be eligible, so the list is
    empty and the field waits.
  */
  const selectedMember = members.find((m) => String(m.id) === userId);
  const eligibleTypes = (
    selectedMember ? (Object.keys(selectedMember.balance) as LeaveType[]) : []
  ).sort((a, b) => MONTHLY_SUMMARY_TYPE_ORDER.indexOf(a) - MONTHLY_SUMMARY_TYPE_ORDER.indexOf(b));

  // A type that stops being eligible when the person changes is not a choice.
  const activeType: LeaveType | "" =
    leaveType && eligibleTypes.includes(leaveType) ? leaveType : "";

  async function onSave() {
    setError(null);
    if (!userId) {
      setError("Pick who this leave is for.");
      return;
    }
    if (!leaveType) {
      setError("Pick which type of leave this is.");
      return;
    }
    try {
      await log.mutateAsync({
        userId: Number(userId),
        startDate,
        endDate,
        reason: reason.trim(),
        leaveType,
      });
      toast.success("Leave logged and marked approved.");
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not log that leave.");
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader
          title="Log leave for an account"
          description="Recorded as already decided — it lands straight as approved and counts against their balance right away."
        />
        <DialogBody className="flex flex-col gap-4">
          <Field label="Person" htmlFor="log-leave-user">
            <Select id="log-leave-user" value={userId} onChange={(e) => setUserId(e.target.value)}>
              <option value="">Select a person…</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name ?? "Unnamed"}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Leave type" htmlFor="log-leave-type">
            <Select
              id="log-leave-type"
              value={activeType}
              onChange={(e) => setLeaveType(e.target.value as LeaveType)}
            >
              <option value="">Select a leave type…</option>
              {eligibleTypes.map((type) => (
                <option key={type} value={type}>
                  {leaveLabel(type)}
                </option>
              ))}
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Start date" htmlFor="log-leave-start">
              <Input
                id="log-leave-start"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </Field>
            <Field label="End date" htmlFor="log-leave-end">
              <Input
                id="log-leave-end"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </Field>
          </div>
          <Field label="Reason" htmlFor="log-leave-reason" hint="Optional for Optional Holiday.">
            <Textarea id="log-leave-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          {error && (
            <p role="alert" className="flex items-start gap-1.5 text-xs text-bad">
              <Icon name="warning-circle" size={16} />
              <span>{error}</span>
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={log.isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSave} loading={log.isPending}>
            Log leave
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The most a balance can be set to — mirrors overrideCeiling() on the server. */
const BALANCE_CEILING: Record<SettableBalanceType, number> = {
  EL: 40,
  CL: 6,
  SL: 6,
  Menstrual: 12,
  OptionalHoliday: 2,
};

/*
  HR states, per leave type, how many days somebody has LEFT, and how many they
  have for the WHOLE YEAR. The second is what lets the balances table say
  "taken this year" for someone whose earlier leave lives in HR's own records.

  Every box starts EMPTY and an empty box changes nothing. The current figure is
  shown under the label instead of being put in the box: pre-filling from the
  summary query would seed async data into state, and a box that looked filled
  in while that query was still loading would save whatever it happened to hold.

  The two are saved apart on the server, so typing only a year's total never
  moves what is left — see PUT /api/admin/leave-balance.
*/
function EditBalancesDialog({
  member,
  onClose,
}: {
  member: LeaveSummaryMember | null;
  onClose: () => void;
}) {
  const save = useSetLeaveBalances();
  const [values, setValues] = useState<Partial<Record<SettableBalanceType, string>>>({});
  const [totals, setTotals] = useState<Partial<Record<SettableBalanceType, string>>>({});
  const [resets, setResets] = useState<Partial<Record<SettableBalanceType, boolean>>>({});
  const [totalClears, setTotalClears] = useState<Partial<Record<SettableBalanceType, boolean>>>({});
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Only the types this person actually has — the balance's keys are the
  // server's eligibility rule, already applied.
  const types = member
    ? SETTABLE_BALANCE_TYPES.filter((type) => member.balance[type] !== undefined)
    : [];

  /** "" for an empty box, the number for a valid one, or what is wrong with it. */
  function parseDays(
    raw: string | undefined,
    type: SettableBalanceType,
    what: string,
  ): number | "" | { error: string } {
    const text = (raw ?? "").trim();
    if (text === "") return "";
    const days = Number(text);
    if (!Number.isFinite(days) || days < 0 || days * 2 !== Math.floor(days * 2)) {
      return { error: `${leaveLabel(type)} ${what}: enter whole or half days, e.g. 3 or 2.5.` };
    }
    if (days > BALANCE_CEILING[type]) {
      return {
        error: `${leaveLabel(type)} ${what} can't be more than ${BALANCE_CEILING[type]} days under the leave policy.`,
      };
    }
    return days;
  }

  async function onSave() {
    if (!member) return;
    setError(null);
    const balances: Partial<Record<SettableBalanceType, number | null>> = {};
    const yearTotals: Partial<Record<SettableBalanceType, number | null>> = {};
    for (const type of types) {
      // What will be left after this save: the new number if one is typed,
      // otherwise what they have now. Unknown once handed back to the policy.
      let left: number | null | undefined = member.balance[type]?.available ?? undefined;

      if (resets[type]) {
        balances[type] = null;
        left = undefined;
      } else {
        const parsed = parseDays(values[type], type, "left");
        if (typeof parsed === "object") return setError(parsed.error);
        if (parsed !== "") {
          balances[type] = parsed;
          left = parsed;
        }
      }

      if (totalClears[type]) {
        yearTotals[type] = null;
      } else {
        const parsed = parseDays(totals[type], type, "year total");
        if (typeof parsed === "object") return setError(parsed.error);
        if (parsed !== "") {
          if (typeof left === "number" && parsed < left) {
            return setError(
              `${leaveLabel(type)}: the year's total (${formatDays(parsed)}) can't be less than what is left (${formatDays(left)}).`,
            );
          }
          yearTotals[type] = parsed;
        }
      }
    }
    if (Object.keys(balances).length + Object.keys(yearTotals).length === 0) {
      setError("Enter at least one number, or reset one to the policy calculation.");
      return;
    }
    try {
      await save.mutateAsync({ userId: member.id, balances, yearTotals, note: note.trim() });
      toast.success(`Leave balances updated for ${member.name ?? "this person"}.`);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save those balances.");
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader
          title={`Edit leave balances${member?.name ? ` · ${member.name}` : ""}`}
          description="Left: the days they can still take this year. It replaces the policy calculation, and only leave approved from now on comes off it. Total for the year: everything they have for the year, so the table can show how much they've taken. Leave a box empty to keep it as it is."
        />
        <DialogBody className="flex flex-col gap-4">
          {!member ? (
            <EmptyState icon="team" title="That account is no longer in the list" />
          ) : (
            types.map((type) => {
              const line = member.balance[type];
              const currentLeft = line
                ? `Now ${formatDays(line.available)} · ${
                    line.setByHr
                      ? `set by ${line.setByHr.setByName ?? "HR"} on ${formatTimestampDate(line.setByHr.setAt, "d MMM")}`
                      : "policy calculation"
                  }`
                : undefined;
              const currentTotal = line?.yearTotal != null ? `Now ${formatDays(line.yearTotal)}` : "Not entered yet";
              return (
                <fieldset key={type} className="flex flex-col gap-2 rounded-lg border border-line p-3">
                  <legend className="px-1 text-[13px] font-medium text-ink">{leaveLabel(type)}</legend>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Left (days)" htmlFor={`balance-${type}`} hint={currentLeft}>
                      <Input
                        id={`balance-${type}`}
                        type="number"
                        inputMode="decimal"
                        min={0}
                        max={BALANCE_CEILING[type]}
                        step={0.5}
                        value={values[type] ?? ""}
                        disabled={!!resets[type]}
                        onChange={(e) => setValues((v) => ({ ...v, [type]: e.target.value }))}
                      />
                    </Field>
                    <Field label="Total for the year (days)" htmlFor={`total-${type}`} hint={currentTotal}>
                      <Input
                        id={`total-${type}`}
                        type="number"
                        inputMode="decimal"
                        min={0}
                        max={BALANCE_CEILING[type]}
                        step={0.5}
                        value={totals[type] ?? ""}
                        disabled={!!totalClears[type]}
                        onChange={(e) => setTotals((v) => ({ ...v, [type]: e.target.value }))}
                      />
                    </Field>
                  </div>
                  {(line?.setByHr || line?.yearTotal != null) && (
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      {line?.setByHr && (
                        <label className="flex items-center gap-2 text-xs text-muted">
                          <input
                            type="checkbox"
                            checked={!!resets[type]}
                            onChange={(e) => setResets((r) => ({ ...r, [type]: e.target.checked }))}
                          />
                          Go back to the policy calculation instead
                        </label>
                      )}
                      {line?.yearTotal != null && (
                        <label className="flex items-center gap-2 text-xs text-muted">
                          <input
                            type="checkbox"
                            checked={!!totalClears[type]}
                            onChange={(e) => setTotalClears((r) => ({ ...r, [type]: e.target.checked }))}
                          />
                          Clear the year&rsquo;s total
                        </label>
                      )}
                    </div>
                  )}
                </fieldset>
              );
            })
          )}
          <Field label="Note" htmlFor="balance-note" hint="Optional — e.g. where the numbers came from.">
            <Textarea id="balance-note" value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
          {error && (
            <p role="alert" className="flex items-start gap-1.5 text-xs text-bad">
              <Icon name="warning-circle" size={16} />
              <span>{error}</span>
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSave} loading={save.isPending} disabled={!member}>
            Save balances
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
